import React, { useState } from "react";
import { KeyRound, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { setPin } from "@/lib/api";
import { useUser } from "@/context/UserContext";
import { useI18n } from "@/i18n/I18nContext";

const GlobalPinModal = () => {
  const { user, refresh } = useUser();
  const { t } = useI18n();
  const [pin, setPinValue] = useState("");
  const [loading, setLoading] = useState(false);

  // Solamente mostramos si el usuario está autenticado y no tiene PIN configurado.
  // Ignoramos a los super_admin o usuarios que ya tengan has_pin.
  if (!user || user.role === "super_admin" || user.role === "pmst_guest" || user.has_pin) {
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (pin.length < 6) {
      return toast.error(t("pin_setup.too_short"));
    }
    setLoading(true);
    try {
      await setPin(pin);
      toast.success(t("pin_setup.saved"));
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("pin_setup.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="pin-setup-modal">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <div className="p-6 sm:p-8">
          <div className="h-12 w-12 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto mb-4">
            <KeyRound className="h-6 w-6 text-emerald-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 text-center tracking-tight">
            {t("pin_setup.title")}
          </h2>
          <p className="text-sm text-slate-600 text-center mt-2 mb-6">
            {t("pin_setup.desc")}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="text"
              autoFocus
              maxLength={6}
              data-testid="pin-setup-input"
              value={pin}
              onChange={(e) => setPinValue(e.target.value.replace(/[^0-9A-Za-z]/g, "").toUpperCase())}
              placeholder={t("pin_setup.placeholder")}
              className="w-full text-center text-2xl font-mono tracking-[0.25em] h-14 rounded-xl border border-slate-300 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/20 uppercase transition-all"
            />
            
            <button
              type="submit"
              data-testid="pin-setup-save"
              disabled={pin.length < 6 || loading}
              className="w-full h-12 bg-emerald-700 hover:bg-emerald-800 text-white font-semibold rounded-xl flex items-center justify-center gap-2 disabled:opacity-50 disabled:pointer-events-none transition-colors"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  <ShieldCheck className="h-5 w-5" />
                  {t("pin_setup.save")}
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default GlobalPinModal;
